/**
 * The dry register — "Dalykiška" in Settings.
 *
 * Used to mean silence: `tone: 'neutral'` turned the whole voice off and
 * every call site fell back to its plain neutral string. It now means the
 * same narrator with the volume down — clipped, precise, faintly
 * disapproving — not a second character and not nothing. See PERSONA.md.
 *
 * Rules on top of the base voice rules (no exclamation, no emoji, a figure in
 * every over-budget line, names only through `voc`, feminine where a gendered
 * form is unavoidable): no „branguti", no English sprinkles, no cast members
 * (Saulius, Katė, Eimantas-specific material stay in the sassy register
 * only). Caps are tighter — toast ≤ 60 characters, screen ≤ 100 — at maximal
 * context.
 *
 * Coverage is intentionally partial. A key with no pool here falls through to
 * the call site's own neutral string (see `resolvePool` in `engine.ts`), so
 * this catalogue is meant to grow one pool at a time rather than ship whole.
 */

import type { SassContext } from '@gramas/shared'
import { fmtKg, itemName, overBy, promiseDay, voc } from '../fmt.js'

type Line = (ctx: SassContext) => string

export const DRY_CATALOG: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'scale_spoke.base': [
    (c) => `Svarstyklės: ${fmtKg(c.weightKg)} kg. Įrašyta.`,
    (c) => `Gauta iš svarstyklių: ${fmtKg(c.weightKg)} kg.`,
    (c) => `${fmtKg(c.weightKg)} kg. Atsiuntė svarstyklės.`,
  ],
  'greeting.morning': [
    (c) => `Rytas${voc(c)}. Diena dar tuščia.`,
    () => 'Rytas. Pusryčiai dar neįrašyti.',
    () => 'Naujas rytas. Skaičiai nuo nulio.',
    (c) => `Labas rytas${voc(c)}.`,
    () => 'Rytas prasidėjo. Dienoraštis laukia.',
    () => 'Rytas. Pirmas įrašas dar priekyje.',
  ],
  'greeting.day': [
    (c) => `Diena${voc(c)}. Pietūs dar neįrašyti.`,
    () => 'Vidurdienis. Pusė dienos praėjo.',
    () => 'Diena tęsiasi. Dienoraštis tylus.',
    () => 'Popietė. Įrašų kol kas nedaug.',
  ],
  'greeting.evening': [
    (c) => `Vakaras${voc(c)}. Diena beveik baigta.`,
    () => 'Vakaras. Metas apibendrinti dieną.',
    () => 'Vakarėja. Likę įrašai dar laukia.',
    () => 'Vakaras. Diena artėja į pabaigą.',
  ],
  'greeting.night': [
    (c) => `Vėlu${voc(c)}. Dar budi.`,
    () => 'Naktis. Diena oficialiai baigta.',
    () => 'Vėlyva valanda. Poilsis būtų naudingesnis.',
    () => 'Naktis. Rytoj — naujas skaičiavimas.',
  ],
  'log_success.base': [
    () => 'Įrašyta.',
    () => 'Priimta.',
    () => 'Užfiksuota.',
    () => 'Gerai. Įrašyta.',
    () => 'Pridėta prie dienos.',
    () => 'Įrašyta į dienyną.',
    () => 'Suskaičiuota.',
    () => 'Įtraukta.',
    () => 'Priimta į apskaitą.',
    () => 'Įrašas priimtas.',
    () => 'Duomenys atnaujinti.',
    () => 'Įrašyta be komentarų.',
  ],
  // Always after a colon. A participle agreeing with an unknown noun is a
  // coin toss Lithuanian does not forgive: „Varškės sūrelis pašalinta" put a
  // feminine ending on a masculine noun, and every product name is a noun
  // this file has never seen.
  'entry_deleted.base': [
    (c) => `Ištrinta: ${itemName(c)}.`,
    (c) => `Pašalinta: ${itemName(c)}.`,
    (c) => `Nebėra: ${itemName(c)}.`,
    (c) => `Išbraukta: ${itemName(c)}.`,
    (c) => `Nurašyta: ${itemName(c)}.`,
    (c) => `Iš dienos: ${itemName(c)}.`,
  ],
  'day_over.small': [
    (c) => `Viršyta ${overBy(c)} kcal.`,
    (c) => `${overBy(c)} kcal virš normos.`,
    (c) => `Viršyta nedaug: ${overBy(c)} kcal.`,
    (c) => `Virš tikslo ${overBy(c)} kcal.`,
    (c) => `${overBy(c)} kcal virš ribos.`,
  ],
  'day_over.big': [
    (c) => `Viršyta ${overBy(c)} kcal.`,
    (c) => `Gerokai viršyta: ${overBy(c)} kcal.`,
    (c) => `${overBy(c)} kcal virš biudžeto.`,
    (c) => `Virš tikslo ${overBy(c)} kcal. Nemažai.`,
    (c) => `${overBy(c)} kcal viršaus.`,
  ],
  // The chat edges: the muzzled voice still converses, in telegrams. Petting
  // and chips have no dry pools on purpose — the animation plays, the chips
  // are simply not offered.
  'chat_excuse.base': [
    () => 'Ryšio nėra. Bandyk vėliau.',
    () => 'Pokalbiai laikinai neveikia.',
    () => 'Atsakyti negaliu. Vėliau.',
  ],
  'chat_tired.base': [
    () => 'Šios dienos pokalbių limitas baigtas.',
    () => 'Šiandien užteks. Rytoj.',
    () => 'Pokalbiai — rytoj.',
  ],
  'chat_exit.base': [() => 'Pokalbis baigtas.', () => 'Baigiam. Iki.', () => 'Viskas šiam kartui.'],
  'chat_rude_exit.base': [
    () => 'Tonas netinkamas. Baigiam.',
    () => 'Pokalbis nutrauktas.',
    () => 'Užteks. Iki rytojaus.',
  ],
  'promise_broken.base': [
    (c) => `Pažadas (${promiseDay(c)}) neišlaikytas.`,
    (c) => `Fiksuoju: pažadas ${promiseDay(c)} sulaužytas.`,
    (c) => `Prieštarauja pažadui, ${promiseDay(c)}`,
  ],
}
